"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import type { User } from "./types";
import { getCurrentUser, logout as apiLogout } from "./api";

type AuthState = {
  user: User | null;
  is_authenticated: boolean;
  is_loading: boolean;
};

interface AuthContextValue extends AuthState {
  login: (user: User) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    is_authenticated: false,
    is_loading: true,
  });

  const refresh = async () => {
    try {
      const res = await getCurrentUser();

      if (res.success && res.data) {
        setAuthState({
          user: res.data.user,
          is_authenticated: true,
          is_loading: false,
        });
        return;
      }

      setAuthState({
        user: null,
        is_authenticated: false,
        is_loading: false,
      });
    } catch {
      setAuthState({
        user: null,
        is_authenticated: false,
        is_loading: false,
      });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const login = (user: User) => {
    setAuthState({
      user,
      is_authenticated: true,
      is_loading: false,
    });
  };

  const logout = async () => {
    try {
      await apiLogout();
    } finally {
      setAuthState({
        user: null,
        is_authenticated: false,
        is_loading: false,
      });
    }
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return ctx;
}